import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/authStore';
import { 
  BookOpen, 
  ExternalLink, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  Sparkles, 
  Building2, 
  Barcode, 
  ShieldCheck, 
  ShoppingBag, 
  CheckCircle2, 
  ChevronDown, 
  ChevronUp,
  FileCheck,
  Globe2,
  Info,
  Lock,
  RotateCcw,
  UserCheck,
  Briefcase,
  User,
  GraduationCap,
  Users,
  Check,
  Tag
} from 'lucide-react';
import { Publication } from '@ftt/shared';

const ITEMS_PER_PAGE = 18;

export const PublicationsPage: React.FC = () => {
  const { isAuthenticated, user, isStaff, isAdmin } = useAuth();

  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedPublisher, setSelectedPublisher] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showIsbnProtocol, setShowIsbnProtocol] = useState(false);

  const { data: publicationsRes, isLoading } = useQuery({
    queryKey: ['public-publications'],
    queryFn: () => api.get('/public/publications').then((res) => res.data.data),
  });

  const publications: Publication[] = publicationsRes || [];

  // Filtered publications
  const filtered = useMemo(() => {
    return publications.filter((book) => {
      if (selectedCategory !== 'ALL' && !book.category?.toLowerCase().includes(selectedCategory.toLowerCase())) {
        return false;
      }
      if (selectedPublisher !== 'ALL' && book.publisher !== selectedPublisher) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchTitle = book.title?.toLowerCase().includes(q);
        const matchAuthor = book.author?.toLowerCase().includes(q);
        const matchDesc = book.description?.toLowerCase().includes(q);
        const matchCat = book.category?.toLowerCase().includes(q);
        const matchPub = book.publisher?.toLowerCase().includes(q);
        const matchIsbn = book.isbn?.toLowerCase().includes(q);
        if (!matchTitle && !matchAuthor && !matchDesc && !matchCat && !matchPub && !matchIsbn) {
          return false;
        }
      }
      return true;
    });
  }, [publications, selectedCategory, selectedPublisher, searchQuery]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
  const paginatedBooks = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };

  const handlePublisherChange = (pub: string) => {
    setSelectedPublisher(pub);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const resetAllFilters = () => {
    setSelectedCategory('ALL');
    setSelectedPublisher('ALL');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const hasActiveFilters = selectedCategory !== 'ALL' || selectedPublisher !== 'ALL' || searchQuery.trim() !== '';

  return (
    <div className="space-y-10 py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans antialiased text-slate-900">
      
      {/* 1. ARCHIVE HEADER & INSTITUTIONAL CONTEXT */}
      <section className="text-center space-y-3 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-50 border border-brand-200 text-brand-800 text-[11px] font-bold uppercase tracking-wider font-mono">
          <Building2 className="w-3.5 h-3.5 text-brand-600" />
          <span>Official Production Archive &bull; FOR THE TRUTH PRESS</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-serif font-black text-slate-900 tracking-tight">
          Titles Produced &amp; Distributed Through FTT
        </h1>
        <p className="text-slate-600 text-xs sm:text-sm leading-relaxed font-medium">
          This official repository documents the {publications.length || 432} theological volumes, puritan classics, authorized Indian co-editions, and regional vernacular translations brought to press, localized, or distributed across India by <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong>.
        </p>
      </section>

      {/* 2. CLARITY CALLOUT: DEDICATED COMMERCIAL STOREFRONT VS CORPORATE ARCHIVE */}
      <section className="p-4 sm:p-5 rounded-2xl bg-amber-50/80 border border-amber-200/90 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-100 border border-amber-300 flex items-center justify-center shrink-0 text-amber-800 mt-0.5">
            <ShoppingBag className="w-4 h-4" />
          </div>
          <div className="space-y-0.5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-amber-900">
              Commercial Retail &amp; Direct Book Purchases
            </h4>
            <p className="text-xs text-amber-800/90 leading-relaxed max-w-2xl font-normal">
              Looking to purchase copies for personal reading, church bookstalls, or study groups? Direct commercial sales, customer cart orders, and pan-India shipping are hosted on our dedicated bookstore storefront at <strong className="font-bold text-amber-950">forthetruth.in</strong>. This platform serves as the corporate publication register, rights archive, and production record.
            </p>
          </div>
        </div>

        <a
          href="https://forthetruth.in/shop"
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs shadow-xs transition-all inline-flex items-center gap-1.5 self-stretch md:self-auto justify-center"
        >
          <span>Visit Commercial Bookstore</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </section>

      {/* 3. TECHNICAL ISBN PROTOCOL COLLAPSIBLE DRAWER (GOVERNMENT OF INDIA - isbn.gov.in) */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden transition-all">
        <button
          onClick={() => setShowIsbnProtocol(!showIsbnProtocol)}
          className="w-full p-4 sm:p-5 flex items-center justify-between gap-4 text-left hover:bg-slate-50/80 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700 shrink-0">
              <Barcode className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-serif font-bold text-sm sm:text-base text-slate-900">
                  Government of India ISBN Allotment &amp; Legal Deposit Protocol
                </h3>
                <span className="hidden sm:inline-block px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 text-[10px] font-mono font-bold border border-emerald-200">
                  isbn.gov.in
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium">
                How FOR THE TRUTH PRESS allocates, verifies, and registers official Indian ISBNs through the Raja Rammohun Roy National Agency
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs font-bold text-brand-700 shrink-0">
            <span className="hidden sm:inline">{showIsbnProtocol ? 'Hide Protocol' : 'Explore Technical Process'}</span>
            {showIsbnProtocol ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>

        {showIsbnProtocol && (
          <div className="p-5 sm:p-6 border-t border-slate-200 bg-slate-50/50 space-y-6 animate-in fade-in duration-200">
            <div className="max-w-3xl space-y-1">
              <span className="text-[10px] font-mono uppercase tracking-widest text-brand-700 font-bold">
                Raja Rammohun Roy National Agency for ISBN &bull; Department of Higher Education, Ministry of Education, Govt. of India
              </span>
              <p className="text-xs text-slate-600 leading-relaxed">
                Under the international ISO 2108 standard and Indian statutory guidelines, every volume published, co-editioned, or translated by <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong> undergoes a strict bibliographic allotment process via <a href="https://isbn.gov.in/" target="_blank" rel="noopener noreferrer" className="text-brand-700 font-bold underline">isbn.gov.in</a> before release.
              </p>
            </div>

            {/* 13-Digit Barcode Anatomy Block */}
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200/90 shadow-2xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                <span className="font-serif font-bold text-sm text-slate-900">
                  Anatomy of a 13-Digit Indian ISBN Barcode (ISO 2108)
                </span>
                <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                  Format: 5 Distinct Elements
                </span>
              </div>

              {/* Hyphenated Number Barcode Preview */}
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-center space-y-2">
                <div className="flex items-center justify-center flex-wrap gap-1 sm:gap-2 font-mono font-black text-sm sm:text-lg">
                  <span className="text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded border border-amber-300">978 (GS1)</span>
                  <span className="text-slate-400">-</span>
                  <span className="text-blue-700 bg-blue-100/80 px-2 py-0.5 rounded border border-blue-300">81 (India)</span>
                  <span className="text-slate-400">-</span>
                  <span className="text-emerald-800 bg-emerald-100/80 px-2 py-0.5 rounded border border-emerald-300">959959 (FTT Press)</span>
                  <span className="text-slate-400">-</span>
                  <span className="text-purple-700 bg-purple-100/80 px-2 py-0.5 rounded border border-purple-300">7 (Title/Vol)</span>
                  <span className="text-slate-400">-</span>
                  <span className="text-rose-700 bg-rose-100/80 px-2 py-0.5 rounded border border-rose-300">1 (Check Digit)</span>
                </div>
              </div>

              {/* Quick 5-Pillar Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5 text-[11px]">
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                  <strong className="text-amber-800 block font-mono">1. GS1 Prefix</strong>
                  <span className="text-slate-600">978 or 979 identifying Bookland worldwide.</span>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                  <strong className="text-blue-800 block font-mono">2. Country Code</strong>
                  <span className="text-slate-600">81 or 93 designated for Indian publications.</span>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                  <strong className="text-emerald-800 block font-mono">3. Registrant</strong>
                  <span className="text-slate-600">Unique publisher identifier for FTT Press.</span>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                  <strong className="text-purple-800 block font-mono">4. Publication</strong>
                  <span className="text-slate-600">Specific title, volume, or binding format.</span>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                  <strong className="text-rose-800 block font-mono">5. Check Digit</strong>
                  <span className="text-slate-600">Modulo 10 scanner mathematical validation.</span>
                </div>
              </div>
            </div>

            {/* Applicant Categories Strip (Highlighting Commercial Publisher) */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-amber-50 to-brand-50/40 border border-amber-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2 text-amber-900 font-bold">
                  <Briefcase className="w-4 h-4 text-brand-700" />
                  <span>Statutory Status: Commercial Publisher (Block Allotment Tier)</span>
                </div>
                <p className="text-slate-600 text-[11px] leading-relaxed">
                  As an incorporated corporate press (CIN registered), FOR THE TRUTH PRESS PRIVATE LIMITED is classified as a <strong>Commercial Publisher</strong> on <strong className="text-slate-800">isbn.gov.in</strong>, eligible for block allotments to support continuous publishing and vernacular translation releases.
                </p>
              </div>
              <span className="shrink-0 px-3 py-1 rounded-full bg-brand-700 text-white font-mono text-[10px] font-bold">
                Commercial Category
              </span>
            </div>

            {/* 6 Technical Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
              
              <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-2xs space-y-2">
                <div className="flex items-center justify-between text-brand-700 font-mono font-bold">
                  <span className="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center text-xs">1</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">KYC Validation</span>
                </div>
                <h4 className="font-bold text-slate-900 text-sm">Publisher Legal Registration</h4>
                <p className="text-slate-600 leading-relaxed text-[11px]">
                  Corporate KYC verification of FOR THE TRUTH PRESS PRIVATE LIMITED (CIN, PAN, Registered Office Address proof) on the Raja Rammohun Roy National Agency portal.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-2xs space-y-2">
                <div className="flex items-center justify-between text-brand-700 font-mono font-bold">
                  <span className="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center text-xs">2</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">Metadata Entry</span>
                </div>
                <h4 className="font-bold text-slate-900 text-sm">Bibliographic Classification</h4>
                <p className="text-slate-600 leading-relaxed text-[11px]">
                  Filing exact title, author/translator identities, language classification, physical trim size, page extent, binding format (Paperback/Hardcover), and Dewey Decimal (DDC)/BISAC categorization.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-2xs space-y-2">
                <div className="flex items-center justify-between text-brand-700 font-mono font-bold">
                  <span className="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center text-xs">3</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">Proof Verification</span>
                </div>
                <h4 className="font-bold text-slate-900 text-sm">Pre-Publication Proof Upload</h4>
                <p className="text-slate-600 leading-relaxed text-[11px]">
                  Mandatory submission of the full Title Page, Imprint/Verso Copyright page (with FTT Press credits and rights licensing), Table of Contents, and formal publisher declaration.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-2xs space-y-2">
                <div className="flex items-center justify-between text-brand-700 font-mono font-bold">
                  <span className="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center text-xs">4</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">Govt. Allotment</span>
                </div>
                <h4 className="font-bold text-slate-900 text-sm">13-Digit ISBN Allocation</h4>
                <p className="text-slate-600 leading-relaxed text-[11px]">
                  Official approval by RRRNA Ministry of Education officers, allocating a unique 13-digit identifier (Prefix: 978-93 / 978-81) and permanent indexing in the Indian National Bibliography.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-2xs space-y-2">
                <div className="flex items-center justify-between text-brand-700 font-mono font-bold">
                  <span className="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center text-xs">5</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">Pre-Press Barcode</span>
                </div>
                <h4 className="font-bold text-slate-900 text-sm">EAN-13 Vector Barcoding</h4>
                <p className="text-slate-600 leading-relaxed text-[11px]">
                  Generation of ISO/IEC 15420 vector barcodes at 300+ DPI with compliant Quiet Zones and Human Readable Interpretation (HRI) typography for pre-press back cover placement.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white border border-slate-200/90 shadow-2xs space-y-2">
                <div className="flex items-center justify-between text-brand-700 font-mono font-bold">
                  <span className="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center text-xs">6</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">Statutory Deposit</span>
                </div>
                <h4 className="font-bold text-slate-900 text-sm">National Library Legal Deposit</h4>
                <p className="text-slate-600 leading-relaxed text-[11px]">
                  Compliance with the Delivery of Books Act, 1954: dispatching statutory copies to the National Library of India (Kolkata) and designated repositories in New Delhi, Mumbai, and Chennai.
                </p>
              </div>

            </div>

            <div className="p-3.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
              <span className="flex items-center gap-1.5 font-medium">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                All 432 catalog volumes carry verified Indian ISBNs or authorized international co-publishing rights.
              </span>
              <a
                href="https://isbn.gov.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-700 hover:text-brand-900 font-bold inline-flex items-center gap-1"
              >
                <span>Visit Raja Rammohun Roy National Agency Portal (isbn.gov.in)</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

          </div>
        )}
      </section>

      {/* 4. SEARCHABLE ARCHIVE & CATALOG CONTROLS (GATED / PRIVATE REPOSITORY) */}
      {!isAuthenticated ? (
        /* PRIVATE / RESTRICTED ACCESS STATE FOR PUBLIC VISITORS */
        <section className="p-8 sm:p-14 rounded-3xl bg-white border border-slate-200/90 shadow-sm text-center space-y-6 max-w-3xl mx-auto">
          <div className="w-14 h-14 rounded-2xl bg-slate-900 text-brand-400 flex items-center justify-center mx-auto shadow-md">
            <Lock className="w-6 h-6" />
          </div>

          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-[10px] font-mono font-bold uppercase tracking-wider">
              <ShieldCheck className="w-3.5 h-3.5 text-brand-600" />
              <span>Private Production Archive</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-serif font-black text-slate-900">
              Restricted Production &amp; Rights Repository
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium max-w-xl mx-auto">
              The complete searchable catalog of 432 production titles, bibliographic classifications, ISBN records, and imprint rights agreements is restricted to contracted authors, publishing partners, and internal staff.
            </p>
          </div>

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/login?redirect=/publications"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-wider shadow-md transition-all"
            >
              <Lock className="w-3.5 h-3.5 text-brand-400" />
              <span>Sign In to Access Production Archive</span>
            </Link>

            <a
              href="https://forthetruth.in/shop"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-brand-50 hover:bg-brand-100 text-brand-800 font-bold text-xs border border-brand-200 transition-all"
            >
              <span>Browse Public Bookstore (forthetruth.in)</span>
              <ExternalLink className="w-3.5 h-3.5 text-brand-600" />
            </a>
          </div>

          <div className="pt-4 border-t border-slate-100 text-[11px] text-slate-400 font-medium">
            Need institutional or partner rights credentials? Contact the corporate desk at <a href="mailto:contact@forthetruth.in" className="text-brand-700 font-bold underline">contact@forthetruth.in</a>.
          </div>
        </section>
      ) : (
        /* AUTHENTICATED ACCESS: FULL SEARCH, FILTER & 432 CATALOG RECORDS */
        <div className="space-y-8 animate-in fade-in duration-300">
          
          {/* Authenticated User Status Bar */}
          <div className="p-3.5 rounded-xl bg-emerald-50/80 border border-emerald-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2 text-emerald-900 font-medium">
              <UserCheck className="w-4 h-4 text-emerald-600" />
              <span>Authenticated Archive Access &bull; Signed in as <strong>{user ? `${user.firstName} ${user.lastName}` : 'Authorized Member'}</strong> ({user?.role || 'CLIENT'})</span>
            </div>
            {(isAdmin || isStaff) && (
              <Link to="/admin/cms/publications" className="text-emerald-800 font-bold hover:underline flex items-center gap-1">
                <span>Manage in Admin CMS</span>
                <span>&rarr;</span>
              </Link>
            )}
          </div>

          {/* Search & Filter Controls */}
          <section className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs space-y-4">
            
            {/* Search Input */}
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="relative flex-1 w-full">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search production archive by title, author, ISBN, or imprint partner..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-900 placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500 focus:bg-white transition-all"
                />
              </div>

              <div className="flex items-center gap-3 text-xs font-bold text-slate-700 shrink-0 self-end md:self-auto">
                <span>Archive count: <strong className="text-slate-900">{filtered.length}</strong> of {publications.length || 432} records</span>
                {hasActiveFilters && (
                  <button
                    onClick={resetAllFilters}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 hover:text-rose-700 hover:underline cursor-pointer"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Reset</span>
                  </button>
                )}
              </div>
            </div>

            {/* Categories Chips */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 text-xs">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mr-1">Classification:</span>
              {[
                { id: 'ALL', label: 'All Classifications' },
                { id: 'Bible Study', label: 'Bible Study' },
                { id: 'Theology', label: 'Theology' },
                { id: 'Christian Living', label: 'Christian Living' },
                { id: 'Devotional', label: 'Devotional & Daily Reading' },
                { id: 'Church Questions', label: 'Church Questions' },
                { id: 'Children', label: 'Children & Family' },
                { id: 'Apologetics', label: 'Apologetics' },
              ].map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                    selectedCategory === cat.id
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Imprint & Publisher Filter */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 text-xs">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mr-1">Imprint / Rights Partner:</span>
              {[
                { id: 'ALL', label: 'All Imprints' },
                { id: 'FOR THE TRUTH', label: 'FOR THE TRUTH' },
                { id: 'THE GOOD BOOK COMPANY', label: 'The Good Book Company' },
                { id: 'SHEPHERD PRESS', label: 'Shepherd Press' },
                { id: '10PUBLISHING', label: '10Publishing' },
                { id: 'GOOD SHEPHERD BOOKS', label: 'Good Shepherd Books' },
              ].map((pub) => (
                <button
                  key={pub.id}
                  onClick={() => handlePublisherChange(pub.id)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    selectedPublisher === pub.id
                      ? 'bg-brand-600 text-white shadow-xs'
                      : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {pub.label}
                </button>
              ))}
            </div>

          </section>

          {/* Catalog Archive Grid */}
          <section>
            {isLoading ? (
              <div className="text-center py-20 text-slate-500 font-mono text-xs">Loading publications archive...</div>
            ) : paginatedBooks.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 space-y-3">
                <p className="text-slate-600 text-sm font-medium">
                  No publication records found matching your active filter combination.
                </p>
                <button
                  onClick={resetAllFilters}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset All Filters</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedBooks.map((book) => (
                  <div
                    key={book.id}
                    className="group flex flex-col justify-between bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-brand-300 hover:shadow-lg transition-all duration-300"
                  >
                    {/* Book Cover Image Area */}
                    <div className="aspect-4/5 bg-slate-50 overflow-hidden relative flex items-center justify-center p-4 border-b border-slate-100">
                      <img
                        src={book.coverImage}
                        alt={book.title}
                        loading="lazy"
                        className="max-h-full max-w-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 bg-slate-900/90 text-white text-[9px] font-mono px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                        {book.format}
                      </div>
                      {book.publisher && (
                        <div className="absolute bottom-2 right-2 bg-white/95 text-slate-800 text-[9px] font-bold px-2 py-0.5 rounded shadow-2xs border border-slate-200">
                          {book.publisher}
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-emerald-950/80 border border-emerald-500/40 text-emerald-300 text-[8px] font-mono px-1.5 py-0.5 rounded uppercase font-semibold">
                        FTT Record
                      </div>
                    </div>

                    {/* Metadata & Technical Record */}
                    <div className="p-5 space-y-3 flex-1 flex flex-col justify-between">
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-[10px] font-mono">
                          <span className="text-brand-700 font-bold uppercase tracking-wider truncate">
                            {book.category}
                          </span>
                          {book.isbn && (
                            <span className="text-slate-400 font-semibold shrink-0">
                              ISBN {book.isbn}
                            </span>
                          )}
                        </div>
                        
                        <h3 className="font-serif text-base font-extrabold text-slate-900 group-hover:text-brand-700 transition-colors line-clamp-2 leading-snug" title={book.title}>
                          {book.title}
                        </h3>
                        <p className="text-xs text-slate-600 font-bold">
                          Author: <span className="text-slate-900">{book.author}</span>
                        </p>
                        <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed pt-0.5 font-medium">
                          {book.description}
                        </p>
                      </div>

                      {/* Actions: Bibliographic Record + Direct Store Link */}
                      <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold">
                        <Link
                          to={`/publications/${book.slug}`}
                          className="text-slate-800 hover:text-brand-700 flex items-center gap-1 font-semibold"
                        >
                          <span>Bibliographic Details</span>
                          <span>&rarr;</span>
                        </Link>

                        {book.purchaseUrl && (
                          <a
                            href={book.purchaseUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-slate-100 hover:bg-brand-50 text-slate-700 hover:text-brand-800 transition-colors text-[11px] font-bold border border-slate-200 hover:border-brand-200"
                            title="Purchase on official bookstore storefront"
                          >
                            <span>Store Page</span>
                            <ExternalLink className="w-3 h-3 text-slate-400" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <section className="flex items-center justify-between bg-white px-6 py-4 rounded-2xl border border-slate-200 shadow-xs">
              <div className="text-xs font-bold text-slate-600">
                Archive Page <strong className="text-slate-900">{currentPage}</strong> of <strong className="text-slate-900">{totalPages}</strong> ({filtered.length} Indexed Titles)
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3.5 py-1.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3.5 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </section>
          )}

        </div>
      )}

    </div>
  );
};
